import React, { useEffect, useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField,
    Button,
    Paper,
    CircularProgress,
    Typography,
    Box,
    Grid,
    Divider,
} from "@mui/material";
import NavBar from "../navbar";
import { useParams, useNavigate } from "react-router-dom";
import config from "../config";
import * as Sentry from "@sentry/react";
import { formatUtc, fromUtcInput, toUtcInput } from "../utc";

const EditToken = () => {
    const { token_id } = useParams(); // Получение token_id из URL
    const navigate = useNavigate();

    const [tokenData, setTokenData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchToken = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + `/admin/tokens?token_id=${token_id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "user-authorization": getSession().token,
                    },
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status} ${response.statusText}`);
                }
                const data = await response.json();
                setTokenData(data[0]);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchToken();
    }, [token_id]);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) {
        return validate_logged_in;
    }

    const handleChange = (field, value) => {
        setTokenData({ ...tokenData, [field]: value });
    };

    const handleSave = async () => {
        const payload = { ...tokenData };
        if (typeof payload.name === "string") {
            payload.name = payload.name.trim();
            if (!payload.name) {
                alert("Name is required");
                return;
            }
            if (payload.name.length > 100) {
                alert("Name must be at most 100 characters");
                return;
            }
        }
        setSaving(true);
        try {
            const response = await fetch(config.apiBaseUrl + `/admin/tokens`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
            }
            alert("Token updated successfully!");
            navigate(`/tokens`);
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to save token: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this token?")) return;

        try {
            const response = await fetch(config.apiBaseUrl + `/admin/tokens?token_id=${token_id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
            }
            alert("Token deleted successfully!");
            navigate(`/tokens`);
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to delete token: ${err.message}`);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 800, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        Edit Token
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        Token Fields
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Token ID"
                                value={tokenData.id || ""}
                                InputProps={{ readOnly: true }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                required
                                label="Name"
                                inputProps={{ maxLength: 100 }}
                                value={tokenData.name ?? ""}
                                onChange={(e) => handleChange("name", e.target.value)}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Points (subscription)"
                                type="number"
                                value={tokenData.points || 0}
                                onChange={(e) => handleChange("points", parseInt(e.target.value, 10) || 0)}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Purchase credits (topup / debt)"
                                type="number"
                                helperText="Can be negative — a negative balance is a debt recovered on the next payment."
                                value={tokenData.purchase_credits ?? 0}
                                onChange={(e) => handleChange("purchase_credits", parseInt(e.target.value, 10) || 0)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Expires At (UTC)"
                                type="datetime-local"
                                value={toUtcInput(tokenData.expires_at)}
                                onChange={(e) => {
                                    const value = fromUtcInput(e.target.value);
                                    if (value) handleChange("expires_at", value);
                                }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Created At (UTC)"
                                value={formatUtc(tokenData.created_at, "")}
                                InputProps={{ readOnly: true }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Balance ID (shared by all tokens of this balance)"
                                value={tokenData.balance_id || ""}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ marginY: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        User Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="User ID"
                                value={tokenData.user?.id || ""}
                                InputProps={{ readOnly: true }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="User Email"
                                value={tokenData.user?.email || ""}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ marginY: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        Plan Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Plan ID"
                                value={tokenData.plan?.id || ""}
                                InputProps={{ readOnly: true }}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Plan Name"
                                value={tokenData.plan?.name || ""}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleDelete}
                        >
                            Delete
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default EditToken;
